import H from "history";
import * as _ from "lodash";
import {
    CommandBarButton, DefaultButton, Dialog, DialogFooter,
    Dropdown,
    FontSizes,
    IDropdownOption,
    IDropdownStyles,
    ITextFieldStyles,
    MessageBarType, PrimaryButton,
    TextField
} from "office-ui-fabric-react";
import { MessageBar, MessageBarButton } from "office-ui-fabric-react/lib-commonjs";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { Prompt } from "react-router";
import { Link } from "react-router-dom";
import { DurableFunctionInstance } from "../../models/DurableFunctionInstance";
import {
    MisDataServiceTypes,
    SalamanderStrategy,
    getMisDataServiceTypeDisplayInfo, SchoolMailroomConfigDto, EditableDtoProperty, MailroomOptions
} from "../../models/SchoolMailroomConfigDto";
import { SubjectExcludes } from "../../models/SchoolWorkloadConfig";
import { SdsProfile } from "../../models/SdsProfile";
import SdsSubject from "../../models/SdsSubject";
import { SubjectMerge } from "../../models/SubjectMerge";
import { UserAccessLevel, userAccessLevelIsAtLeast } from "../../models/UserAccessRecord";
import Actions from "../../redux/Actions";
import { IRootState } from "../../store";
import DispatchProps from "../../utils/DispatchProps";
import ApiService from "../ApiService";
import LoadingCard from "../LoadingCard";
import SubjectMergeEditor from "../SubjectMergeEditor";
import SubjectRenameEditor from "../SubjectRenameEditor";
import UserSelector from "../UserSelector";
import { UserOption } from "../UserSelectorBase";
import { MailroomExcludeEditor } from "./MailroomExcludeEditor";
import SalamanderOptionsEditor from "./SalamanderOptionsEditor";
import {SalamanderStrategyEditor} from "./SalamanderStrategyEditor";
import SdsBlankCsvDownload from "./SdsBlankCsvDownload";
import {ConditionalConfigItem} from "./ConditonalConfigItem";
import {Rule} from "../../models/mailroom-school-config-rules/Rule";
import {ConfigNameBuilder} from "./ConfigNameBuilder";
import {MailroomSchoolConfig} from "../../models/MailroomSchoolConfig";
import { ConfigDropdown } from "./ConfigDropdown";
import {AadJoinConfigEditor} from "./AadJoinConfigEditor";

export interface MailroomConfigureProps {
    schoolCode: string;
    tenantId: string;

    usersCdbAccessLevel?: UserAccessLevel;

    history: H.History;

    apiService: ApiService;
}

export interface MailroomConfigureState {
    configRules?: Rule[];
    sdsProfiles?: SdsProfile[];
    automationLoopInstance?: DurableFunctionInstance | null;

    defaultStrategy?: SalamanderStrategy;
    
    originalDto?: SchoolMailroomConfigDto;
    editedDto?: SchoolMailroomConfigDto;

    subjectMergeEditType: "merges" | "renames";

    sdsSubjects?: SdsSubject[];
    loadingSdsSubjects: boolean;

    saveInProgress: boolean;
    previewInProgress: boolean;

    validAcademicYearMessage: string | undefined;

    canSaveConfig: boolean;
    refreshingAutomationStatus: boolean;
    
    showDeleteDialog: boolean;
}

class MailroomConfigure extends Component<MailroomConfigureProps & DispatchProps, MailroomConfigureState> {

    unsavedChangesEventListener = (e: BeforeUnloadEvent) => {
        if (this.areUnsavedChanges()) {
            (e || window.event).returnValue = true; //Gecko + IE
            return true; //Gecko + Webkit, Safari, Chrome etc.
        }
        return false;
    }

    constructor(props: MailroomConfigureProps & DispatchProps) {
        super(props);

        this.state = {
            saveInProgress: false,
            configRules: undefined,
            previewInProgress: false,
            validAcademicYearMessage: undefined,
            loadingSdsSubjects: false,
            subjectMergeEditType: "merges",
            canSaveConfig: true,
            refreshingAutomationStatus: false,
            showDeleteDialog: false
        };
    }

    public async componentDidMount() {
        window.addEventListener("beforeunload", this.unsavedChangesEventListener);
        if (this.props.tenantId && this.props.schoolCode) {

            try {
                const defaultStrategy = await this.props.apiService.getSalamanderStrategiesFromStore();
                this.setState({ defaultStrategy: defaultStrategy });
            }
            catch (e) {
                console.log(e);
                this.props.dispatch(Actions.showMessageBar(`Error loading strategies`, MessageBarType.error));
            }
            
            await this.loadConfigAndMergesAndAutomation();
        }
    }


    componentWillUnmount() {
        window.removeEventListener("beforeunload", this.unsavedChangesEventListener);
    }

    async componentDidUpdate(prevProps: Readonly<MailroomConfigureProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.state.automationLoopInstance && this.state.automationLoopInstance !== prevState.automationLoopInstance) {
            this.setState({
                canSaveConfig: this.state.automationLoopInstance?.runtimeStatus === "Running" ? false : true
            });
        }
    }

    private async loadConfigAndMergesAndAutomation() {
        const configDto = await this.props.apiService.getSchoolMailroomConfigDto(this.props.tenantId, this.props.schoolCode);
        const configRules = await this.props.apiService.getSchoolMailroomConfigRules();
        
        if (configDto.mailroomSchoolConfig?.sdsProfileId) {
            const schoolMailroomAutomation = await this.props.apiService.getAutomation(this.props.tenantId, configDto.mailroomSchoolConfig.sdsProfileId!);
            this.setState({ automationLoopInstance: schoolMailroomAutomation });
        }
        else {
            this.setState({ automationLoopInstance: undefined });
        }

        this.setState({
            configRules: configRules,
            originalDto: configDto,
            editedDto: configDto,
        });
    }


    private async refreshAutomationStatus()
    {
        this.setState({
            refreshingAutomationStatus: true
        });
        
        try
        {
            const automationLoopInstance = await this.props.apiService.getAutomation(this.props.tenantId, this.state.originalDto?.mailroomSchoolConfig.sdsProfileId!);
            this.setState({ automationLoopInstance: automationLoopInstance });
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Failed to refresh automation status : ${e}`, MessageBarType.error));
        }

        this.setState({
            refreshingAutomationStatus: false
        });
    }


    public getProfileDropdownOptions(): IDropdownOption[] {

        const userIsEngineerPlus = userAccessLevelIsAtLeast("engineerplus",this.props.usersCdbAccessLevel)
        const newSds2025Option = {
            key: "CDB-2025",
            text: "New SDS 2025",
            data: "CDB-2025"
        }
        const newSds2026Option = {
            key: "CDB-2026",
            text: "New SDS 2026",
            data: "CDB-2026"
        }
        const noneSdsOption = {
            key: "",
            text: "None",
            data: null
        }
        
        if (this.state.sdsProfiles == undefined) {
            return userIsEngineerPlus ? [newSds2025Option, newSds2026Option, noneSdsOption] : [];
        }

        // Is state.sdsProfiles even used anymore?
        const profileOptions = this.state.sdsProfiles.map((profile) => {
            return {
                key: profile.id,
                text: profile.displayName,
                data: profile.id
            };
        });
        
        return profileOptions;
    }
    public getMisDataServiceTypesDropdownOptions(): IDropdownOption[] {
        return Object.keys(MisDataServiceTypes)
            .filter((key) => isNaN(Number(key)))
            .map((key) => {
                const displayInfo = getMisDataServiceTypeDisplayInfo(MisDataServiceTypes[key as keyof typeof MisDataServiceTypes]);
                return {
                    key: key,
                    text: `${displayInfo.name} - ${displayInfo.detail}`
                };
            });
    }

    public dropdownStyle: Partial<IDropdownStyles> = {
        dropdown: { width: 300 },
    };

    public textFieldStyles: Partial<ITextFieldStyles> = {
        fieldGroup: { width: 300 },
    };
    
    
    private patchEditedDto = (path: [EditableDtoProperty] | [EditableDtoProperty,string],value: any): void => {
        const newConfigDto = _.cloneDeep(this.state.editedDto!);
        _.set(newConfigDto,path,value)
        
        this.setState({
            editedDto: newConfigDto
        });
    }
    
    public onStrategyChanged = (newStrategy: SalamanderStrategy): void => this.patchEditedDto(["strategy"],newStrategy);
    
    public onProfileChange = (event: any, option: any): void => this.patchEditedDto(["mailroomSchoolConfig","sdsProfileId"],option.data as string);
    
    public onMisDataServiceTypeChanged = (event: any, option: any): void => this.patchEditedDto(["mailroomSchoolConfig","misDataServiceType"],option.key as string)
    
    public onAcademicYearChange = (events: any, newValue?: string): void => {

        if (newValue == undefined) {
            this.setState({ validAcademicYearMessage: "Must provide an academic year" });
        }
        else if (newValue.match(/^(?:19|20)\d{2}$/)?.length != 1) {
            this.setState({ validAcademicYearMessage: "Must be a valid academic year" });
        }
        else {
            this.setState({ validAcademicYearMessage: undefined });
        }

        this.patchEditedDto(["mailroomSchoolConfig","academicYear"],newValue);
    }

    public onOptionsChanged = (newOptions: MailroomOptions): void => this.patchEditedDto(["mailroomSchoolConfig","options"],newOptions);
    
    public onInjectedTeachersChanged = (selectedUsers: UserOption[]): void => {
        // Get ids and remove duplicates
        var userIds = _.uniq(selectedUsers.map(user => user.id));

        this.patchEditedDto(["mailroomSchoolConfig","injectedTeachers"],userIds);
    }

    public onExcludedUserPhotosChanged = (selectedUsers: UserOption[]): void => {
        // Get ids and remove duplicates
        var userIds = _.uniq(selectedUsers.map(user => user.id));

        this.patchEditedDto(["mailroomSchoolConfig","excludedUserPhotos"],userIds);
    }
    
    private areUnsavedChanges(): boolean {
        return this.state.editedDto !== undefined && this.state.originalDto !== this.state.editedDto;
    }

    private unsavedChangesAreValidAndCanBeSaved(): boolean {
        return this.areUnsavedChanges() &&
            this.state.validAcademicYearMessage === undefined &&
            this.state.saveInProgress === false;
    }

    public save = async (): Promise<void> => {
        try {
            if (this.unsavedChangesAreValidAndCanBeSaved()) {

                this.setState({
                    saveInProgress: true
                });

                await this.props.apiService.saveSchoolMailroomConfig(this.props.tenantId, this.props.schoolCode, this.state.editedDto!);
                this.props.dispatch(Actions.showMessageBar(`Succesfully saved config and merges`, MessageBarType.success));

                await this.loadConfigAndMergesAndAutomation();
            }
        }
        catch (e) {
            console.log(e);
            if (e.message.includes("401")) {
                this.props.dispatch(Actions.showMessageBar(`Error saving config (Only admins can create/update custom configs)`, MessageBarType.error));
            }
            else {
                this.props.dispatch(Actions.showMessageBar(`Error saving config and merges, check config and merges are valid and automation is not running: ${e.message}`, MessageBarType.error));
            }

        }
        finally {
            this.setState({
                saveInProgress: false
            });
        }
    }

    public previewStrategyAndOptions = async (): Promise<void> => {
        try {

            this.setState({
                previewInProgress: true
            });

            if (this.state.editedDto !== undefined) {

                const overrides: Partial<MailroomSchoolConfig> = {
                    strategy: this.state.editedDto.strategy,
                    options: this.state.editedDto.mailroomSchoolConfig.options,
                    academicYear: this.state.editedDto.mailroomSchoolConfig.academicYear,
                    excludeClassesWithSubjectCodes: this.state.editedDto.mailroomSchoolConfig.excludeClassesWithSubjectCodes,
                    subjectMerges: this.state.editedDto.subjectMerges,
                    injectedTeachers: this.state.editedDto.mailroomSchoolConfig.injectedTeachers,
                    excludedUserPhotos: this.state.editedDto.mailroomSchoolConfig.excludedUserPhotos,
                    misDataServiceType: this.state.editedDto.mailroomSchoolConfig.misDataServiceType,

                    className: this.state.editedDto!.mailroomSchoolConfig.className,
                    regGroupClassName: this.state.editedDto!.mailroomSchoolConfig.regGroupClassName,
                    subjectYearClassName: this.state.editedDto!.mailroomSchoolConfig.subjectYearClassName,
                    pastoralHouseClassName: this.state.editedDto!.mailroomSchoolConfig.pastoralHouseClassName,
                    userDefinedGroupClassName: this.state.editedDto!.mailroomSchoolConfig.userDefinedGroupClassName,
                    yearClassName: this.state.editedDto!.mailroomSchoolConfig.yearClassName,

                    yearGroupCourseOption: this.state.editedDto!.mailroomSchoolConfig.yearGroupCourseOption,
                    studentAadJoinConfig: this.state.editedDto!.mailroomSchoolConfig.studentAadJoinConfig,
                    teacherAadJoinConfig: this.state.editedDto!.mailroomSchoolConfig.teacherAadJoinConfig,
                    regGroupCourseOption: this.state.editedDto!.mailroomSchoolConfig.regGroupCourseOption,
                    classCourseOption: this.state.editedDto!.mailroomSchoolConfig.classCourseOption,
                    teacherSdsUserIdOption: this.state.editedDto!.mailroomSchoolConfig.teacherSdsUserIdOption,
                    teacherEmailOption: this.state.editedDto!.mailroomSchoolConfig.teacherEmailOption,
                    studentSdsUserIdOption: this.state.editedDto!.mailroomSchoolConfig.studentSdsUserIdOption,
                    studentEmailOption: this.state.editedDto!.mailroomSchoolConfig.studentEmailOption,
                    guardianPhoneNumberOption: this.state.editedDto!.mailroomSchoolConfig.guardianPhoneNumberOption,
                    studentGradeOption: this.state.editedDto!.mailroomSchoolConfig.studentGradeOption,
                };
                const result = await this.props.apiService.triggerMailroomProcessor(this.props.tenantId, this.props.schoolCode, overrides);
                this.props.dispatch(Actions.showMessageBar(`Started processor with id ${result.id}`, MessageBarType.success));
                window.open(
                    `/cdb-admin/mailroom/salamander/${this.props.tenantId}/${this.props.schoolCode}/process/${result.id}`,
                    "_blank",
                );
            }
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error previewing strategy and options`, MessageBarType.error));
        }
        finally {
            this.setState({
                previewInProgress: false
            });
        }
    }

    private renderMailroomConfig() {

        if (!this.state.originalDto) {
            return <LoadingCard />;
        }

        if (this.state.originalDto.strategyError == true) {
            return <div style={{ color: "red" }}>
                <div>
                    Error when loading strategy
                </div>
                <div>
                    {this.state.originalDto.strategyErrorReason}
                </div>
            </div>
        }

        return (
            <div>
                <Dropdown
                    placeholder="Select an option"
                    label="MIS Data Source"
                    selectedKey={this.state.editedDto?.mailroomSchoolConfig.misDataServiceType}
                    options={this.getMisDataServiceTypesDropdownOptions()}
                    styles={this.dropdownStyle}
                    onChange={this.onMisDataServiceTypeChanged} />

                <div style={{ marginTop: "0.5rem", fontSize: FontSizes.small }}>
                    <Link to={`/cdb-admin/mis-data/${this.props.tenantId}/${this.props.schoolCode}`} target="_blank">
                        Click to view or manage MIS data
                    </Link>
                </div>
                
                <TextField
                    label="Academic Year"
                    value={this.state.editedDto?.mailroomSchoolConfig.academicYear}
                    onChange={this.onAcademicYearChange}
                    styles={this.textFieldStyles}
                    disabled={this.state.saveInProgress} />

                <div style={{ marginTop: "0.5rem", fontSize: FontSizes.small, color: "red" }}>
                    {this.state.validAcademicYearMessage}
                </div>
                
                <Dropdown
                    placeholder="Select an option"
                    label="SDS Flow"
                    defaultSelectedKey={this.state.editedDto?.mailroomSchoolConfig.sdsProfileId || ""}
                    selectedKey={this.state.editedDto?.mailroomSchoolConfig.sdsProfileId}
                    options={this.getProfileDropdownOptions()}
                    styles={this.dropdownStyle}
                    onChange={this.onProfileChange} />
                

                <div style={{ marginTop: "0.5rem", fontSize: FontSizes.small }}>
                    <SdsBlankCsvDownload tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} />
                </div>

                <h4>Excludes</h4>

                <MailroomExcludeEditor items={this.state.editedDto?.mailroomSchoolConfig.excludeClassesWithSubjectCodes || []}
                    disabled={this.state.saveInProgress}
                    onChange={(newSubjectCodes) => {
                        this.patchEditedDto(["mailroomSchoolConfig","excludeClassesWithSubjectCodes"],newSubjectCodes);
                    }}
                    title={"Exclude Subject Codes"}
                    header={"Subject code"} />

                <UserSelector
                    label="Users to exclude from photos (Please remember to remove photo(s) from CDBLists)"
                    tenantId={this.props.tenantId}
                    className="m-top-05rem"
                    disabled={this.state.saveInProgress}
                    multiSelect={true}
                    selectedUsers={this.state.editedDto?.mailroomSchoolConfig.excludedUserPhotos}
                    onSelectionChanged={this.onExcludedUserPhotosChanged} />

                <UserSelector
                    label="Additional users to inject as teachers"
                    tenantId={this.props.tenantId}
                    className="m-top-05rem"
                    disabled={this.state.saveInProgress}
                    multiSelect={true}
                    selectedUsers={this.state.editedDto?.mailroomSchoolConfig.injectedTeachers}
                    onSelectionChanged={this.onInjectedTeachersChanged} />

                <h4>
                    Options
                </h4>

                <SalamanderOptionsEditor
                    options={this.state.editedDto?.mailroomSchoolConfig.options}
                    onOptionsChanged={this.onOptionsChanged}
                    disabled={this.state.saveInProgress} />

                {this.renderClassNamesArea()}
                
                {this.renderUsersArea()}
                
                <ConditionalConfigItem path={["parentGuardian"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} >
                    {() => {
                        return <Fragment>
                            <h4>
                                Parent/Guardian
                            </h4>

                            <ConfigDropdown
                                path={["parentGuardian", "guardianPhoneNumberOption"]}
                                config={this.state.editedDto?.mailroomSchoolConfig!}
                                configRules={this.state.configRules!}
                                label="Phone number"
                                selectedKey={this.state.editedDto?.mailroomSchoolConfig.guardianPhoneNumberOption}
                                onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "guardianPhoneNumberOption"], key)} />
                        </Fragment>
                    }}
                </ConditionalConfigItem>

                <ConditionalConfigItem path={["classes"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} >
                    {() => {
                        return <Fragment>
                            <h4>
                                Classes
                            </h4>

                            <ConfigDropdown
                                path={["classes", "classCourseOption"]}
                                config={this.state.editedDto?.mailroomSchoolConfig!}
                                configRules={this.state.configRules!}
                                label="Classes course option"
                                selectedKey={this.state.editedDto?.mailroomSchoolConfig.classCourseOption}
                                onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "classCourseOption"], key)} />
                        </Fragment>
                    }}
                </ConditionalConfigItem>

                <ConditionalConfigItem path={["regGroup"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} >
                    {() => {
                        return <Fragment>
                            <h4>
                                Registration Classes
                            </h4>

                            <ConfigDropdown
                                path={["regGroup", "regGroupCourseOption"]}
                                config={this.state.editedDto?.mailroomSchoolConfig!}
                                configRules={this.state.configRules!}
                                label="Registration group course option"
                                selectedKey={this.state.editedDto?.mailroomSchoolConfig.regGroupCourseOption}
                                onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "regGroupCourseOption"], key)} />
                        </Fragment>
                    }}
                </ConditionalConfigItem>

                <ConditionalConfigItem path={["yearGroup"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} >
                    {() => {
                        return <Fragment>
                            <h4>
                                Year Classes
                            </h4>

                            <ConfigDropdown
                                path={["yearGroup", "yearGroupCourseOption"]}
                                config={this.state.editedDto?.mailroomSchoolConfig!}
                                configRules={this.state.configRules!}
                                label="Year group course option"
                                selectedKey={this.state.editedDto?.mailroomSchoolConfig.yearGroupCourseOption}
                                onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "yearGroupCourseOption"], key)} />
                        </Fragment>
                    }}
                </ConditionalConfigItem>
                

                <ConditionalConfigItem path={["strategy"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} >
                {() => {
                    return (
                        <Fragment>
                            <h4>
                                Salamander Strategy
                            </h4>
                            
                            <SalamanderStrategyEditor
                                configRules={this.state.configRules!}
                                strategy={this.state.editedDto?.strategy || this.state.defaultStrategy!}
                                options={this.state.editedDto?.mailroomSchoolConfig.options}
                                onStrategyChanged={this.onStrategyChanged}
                                disabled={this.state.saveInProgress}/>
                        </Fragment>
                    );
                }}
                </ConditionalConfigItem>

            </div>
        );

    }
    
    private renderUsersArea() : JSX.Element
    {
        const studentAadJoin = this.state.editedDto?.mailroomSchoolConfig?.options.useAadStudentJoin;
        const teacherAadJoin = this.state.editedDto?.mailroomSchoolConfig?.options.useAadTeacherJoin;
        const aadJoinMessage = "AAD Join Enabled (This option is not used)";
        
        return (
            <Fragment>
                <h4>
                    Teachers
                </h4>
                
                <ConfigDropdown
                    path={["teacherSdsUserIdOption"]}
                    config={this.state.editedDto?.mailroomSchoolConfig!}
                    configRules={this.state.configRules!}
                    label="Teacher SDS ID"
                    selectedKey={this.state.editedDto?.mailroomSchoolConfig.teacherSdsUserIdOption}
                    onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "teacherSdsUserIdOption"], key)} />
                
                <ConfigDropdown
                    path={["teacherEmailOption"]}
                    config={this.state.editedDto?.mailroomSchoolConfig!}
                    configRules={this.state.configRules!}
                    label={teacherAadJoin ? aadJoinMessage : "Teacher email"}
                    disabled={teacherAadJoin}
                    selectedKey={this.state.editedDto?.mailroomSchoolConfig.teacherEmailOption}
                    onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "teacherEmailOption"], key)} />

                <ConditionalConfigItem path={["teacherAadJoinConfig"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!}>
                    {() => {
                        return (
                            <Fragment>
                                <AadJoinConfigEditor userNoun={"Teacher"} path={"teacherAadJoinConfig"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} aadJoinConfigChanged={(a) => {
                                    this.patchEditedDto(["mailroomSchoolConfig","teacherAadJoinConfig"],a);
                                }}/>
                            </Fragment>
                        );
                    }}
                </ConditionalConfigItem>


                <h4>
                    Students
                </h4>

                <ConfigDropdown
                    path={["studentSdsUserIdOption"]}
                    config={this.state.editedDto?.mailroomSchoolConfig!}
                    configRules={this.state.configRules!}
                    label="Student SDS ID"
                    selectedKey={this.state.editedDto?.mailroomSchoolConfig.studentSdsUserIdOption}
                    onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "studentSdsUserIdOption"], key)} />

                <ConfigDropdown
                    path={["studentGradeOption"]}
                    config={this.state.editedDto?.mailroomSchoolConfig!}
                    configRules={this.state.configRules!}
                    label="Grade"
                    selectedKey={this.state.editedDto?.mailroomSchoolConfig.studentGradeOption}
                    onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "studentGradeOption"], key)} />

                <ConfigDropdown
                    path={["studentEmailOption"]}
                    config={this.state.editedDto?.mailroomSchoolConfig!}
                    configRules={this.state.configRules!}
                    label={studentAadJoin ? aadJoinMessage : "Student email"}
                    disabled={studentAadJoin}
                    selectedKey={this.state.editedDto?.mailroomSchoolConfig.studentEmailOption}
                    onChange={(key) => this.patchEditedDto(["mailroomSchoolConfig", "studentEmailOption"], key)} />

                <ConditionalConfigItem path={["studentAadJoinConfig"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!}>
                    {() => {
                        return (
                            <Fragment>
                                <AadJoinConfigEditor userNoun={"Student"} path={"studentAadJoinConfig"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} aadJoinConfigChanged={(a) => {
                                    this.patchEditedDto(["mailroomSchoolConfig","studentAadJoinConfig"],a);
                                }}/>
                            </Fragment>
                        );
                    }}
                </ConditionalConfigItem>
                
                
            </Fragment>
        )
    }
    
    private renderClassNamesArea() : JSX.Element
    {
        const onConfigChanged = (newEditedConfig: MailroomSchoolConfig) => {
            this.patchEditedDto(["mailroomSchoolConfig"],newEditedConfig)
        }
        
        return (
            <ConditionalConfigItem path={["classNames"]} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!}>
                {() => {
                    return (
                        <Fragment>
                            <h4>
                                Class Names
                            </h4>

                            <ConfigNameBuilder namePath={"className"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} onConfigChanged={onConfigChanged}/>
                            <ConfigNameBuilder namePath={"regGroupClassName"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} onConfigChanged={onConfigChanged}/>
                            <ConfigNameBuilder namePath={"yearClassName"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} onConfigChanged={onConfigChanged}/>
                            <ConfigNameBuilder namePath={"pastoralHouseClassName"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} onConfigChanged={onConfigChanged}/>
                            <ConfigNameBuilder namePath={"subjectYearClassName"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} onConfigChanged={onConfigChanged}/>
                            <ConfigNameBuilder namePath={"userDefinedGroupClassName"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} onConfigChanged={onConfigChanged} />
                            <ConfigNameBuilder namePath={"clubClassName"} config={this.state.editedDto?.mailroomSchoolConfig!} configRules={this.state.configRules!} onConfigChanged={onConfigChanged} />
                            
                        </Fragment>
                    );
                }}
            </ConditionalConfigItem>
        );
    }

    private async startEditingSubjectMerges() {
        if (this.state.editedDto === undefined) {
            return;
        }

        const overrides: Partial<MailroomSchoolConfig> = {
            strategy: this.state.editedDto!.strategy,
            options: this.state.editedDto!.mailroomSchoolConfig.options,
            academicYear: this.state.editedDto!.mailroomSchoolConfig.academicYear,
            excludeClassesWithSubjectCodes: this.state.editedDto!.mailroomSchoolConfig.excludeClassesWithSubjectCodes,
            subjectMerges: this.state.editedDto?.subjectMerges,
            
            className: this.state.editedDto!.mailroomSchoolConfig.className,
            regGroupClassName: this.state.editedDto!.mailroomSchoolConfig.regGroupClassName,
            subjectYearClassName: this.state.editedDto!.mailroomSchoolConfig.subjectYearClassName,
            pastoralHouseClassName: this.state.editedDto!.mailroomSchoolConfig.pastoralHouseClassName,
            userDefinedGroupClassName: this.state.editedDto!.mailroomSchoolConfig.userDefinedGroupClassName,
            yearClassName: this.state.editedDto!.mailroomSchoolConfig.yearClassName,
            teacherAadJoinConfig: this.state.editedDto.mailroomSchoolConfig.teacherAadJoinConfig,
            studentAadJoinConfig: this.state.editedDto.mailroomSchoolConfig.studentAadJoinConfig,
            yearGroupCourseOption: this.state.editedDto.mailroomSchoolConfig.yearGroupCourseOption,
            regGroupCourseOption: this.state.editedDto.mailroomSchoolConfig.regGroupCourseOption,
            classCourseOption: this.state.editedDto.mailroomSchoolConfig.classCourseOption,
            guardianPhoneNumberOption: this.state.editedDto.mailroomSchoolConfig.guardianPhoneNumberOption,
        };

        this.setState({
            loadingSdsSubjects: true
        });

        try {
            const sdsSubjects = await this.props.apiService.previewSubjectsInMailroomInbox(this.props.tenantId, this.props.schoolCode, overrides);
            this.setState({
                sdsSubjects: sdsSubjects
            });
        }
        catch (e) {
            await this.props.dispatch(Actions.showMessageBar(`Failed to read subjects from inbox files, check that the course/subject strategies correct and the files contain subject data : ${e}`, MessageBarType.error));
        }

        this.setState({ loadingSdsSubjects: false });
    }

    private renderSubjectEditor() {

        let editor = undefined;

        if ((this.state.originalDto?.subjectMerges !== undefined || this.state.editedDto?.subjectMerges !== undefined) && this.state.sdsSubjects !== undefined) {
            const subjectExcludes: SubjectExcludes = {
                sourceSubjectCodes: this.state.editedDto?.mailroomSchoolConfig.excludeClassesWithSubjectCodes || []
            };

            const EditorComponent = this.state.subjectMergeEditType === "merges" ? SubjectMergeEditor : SubjectRenameEditor;

            editor = (
                <EditorComponent
                    onMergesChanged={(subjectMerges: SubjectMerge[]) => {
                        this.patchEditedDto(["subjectMerges"],subjectMerges);
                    }}
                    sdsSubjects={this.state.sdsSubjects!}
                    subjectMerges={this.state.editedDto?.subjectMerges || this.state.originalDto?.subjectMerges!}
                    subjectExcludes={subjectExcludes}
                />
            );
        }

        const startEditingButton = (
            <CommandBarButton
                iconProps={{ iconName: "edit" }}
                text="Start Editing"
                disabled={this.state.loadingSdsSubjects}
                onClick={(e) => this.startEditingSubjectMerges()} />
        );


        const swapEditorButton = (
            <CommandBarButton
                iconProps={{ iconName: this.state.subjectMergeEditType === "merges" ? "Edit" : "MergeDuplicate" }}
                text={this.state.subjectMergeEditType === "merges" ? "Rename Subjects" : "Merge Subjects"}
                disabled={this.state.loadingSdsSubjects}
                onClick={(e) => {
                    if (this.state.subjectMergeEditType === "merges") {
                        this.setState({
                            subjectMergeEditType: "renames"
                        });
                    }
                    else {
                        this.setState({
                            subjectMergeEditType: "merges"
                        });
                    }
                }} />
        );

        return (
            <div className="card">
                <div className="card__header">
                    <span className={"card__title"}>{this.state.subjectMergeEditType === "renames" ? "Rename Subjects" : "Merge Subjects"}</span>
                    {this.state.sdsSubjects === undefined ? startEditingButton : swapEditorButton}
                </div>
                <div className="card__content">
                    {this.state.sdsSubjects === undefined ? <span>Click "Start Editing" to merge and rename subjects. This retrieves the subjects from the inbox files using the selected course strategies.</span> : null}
                    {this.state.loadingSdsSubjects ? <LoadingCard /> : null}
                    {editor}
                </div>
            </div>
        );
    }

    private renderSaveDisabledMessageBar() {
        return (
            <MessageBar
                className={"message-bar--inner-text-flex"}
                messageBarType={MessageBarType.blocked}
                isMultiline={true}
            >
                <div className={"flex-item--flex-1"}>
                    <span className={"text-tiny"}><b>Mailroom config cannot be saved while automation is running</b><br></br>
                        <b>Please ask an admin to disable the automation to unlock this page. You can still hit preview processor at the top of the page if you just want to run a new processor</b>
                    </span>
                </div>
                <MessageBarButton
                    disabled={this.state.refreshingAutomationStatus}
                    onClick={async () => {
                        await this.refreshAutomationStatus();
                    }}>
                    Refresh automation status
                </MessageBarButton>
            </MessageBar>
        );
    }
    
    private renderDeleteConfigCard(): JSX.Element {
        const userIsAdmin = userAccessLevelIsAtLeast("admin",this.props.usersCdbAccessLevel)
        
        return (
            <div className={`card`}>
                <div className="card__header">
                    <span className={"card__title"}>Remove {this.state.originalDto?.mailroomSchoolConfig.school?.schoolName} from mailroom</span>
                </div>

                <MessageBar
                    className={"message-bar--inner-text-flex"}
                    messageBarType={MessageBarType.blocked}
                    isMultiline={true}
                >
                    <span>This will delete the configuration above. Inbox files, subject merges and processor/upload/automation instances will not be deleted.</span>
                </MessageBar>
                
                <div className="card__content flex-row flex--justify-content-center">
                    <PrimaryButton
                        text="Remove from mailroom"
                        disabled={!userIsAdmin}
                        onClick={async () => {
                            this.setState({
                                showDeleteDialog: true
                            });
                        }}
                    />
                </div>
            </div>
        );
    }
    
    private renderDeleteConfigDialog(): JSX.Element
    {
        const schoolName = this.state.originalDto?.mailroomSchoolConfig.school?.schoolName;
        
        const closeDialog = () => {
            this.setState({
                showDeleteDialog: false
            });
        }
        
        return (
            <Dialog
                hidden={!this.state.showDeleteDialog}
                onDismiss={closeDialog}
                dialogContentProps={{
                    title: `Remove ${schoolName} from mailroom?`,
                    subText: "Are you sure?"
                }}>

                <DialogFooter>
                    <PrimaryButton onClick={async () => {
                        try
                        {
                            await this.props.apiService.deleteSchoolMailroomConfig(this.props.tenantId,this.props.schoolCode);
                            await this.props.dispatch(Actions.showMessageBar(`Successfully removed ${this.state.originalDto?.mailroomSchoolConfig.school?.schoolName} from mailroom`,MessageBarType.success));
                            closeDialog();

                            this.props.history.push("/cdb-admin/mailroom");
                        }
                        catch(e)
                        {
                            await this.props.dispatch(Actions.showMessageBar(`Failed to remove from mailroom: ${e.message}`,MessageBarType.error));
                            closeDialog();
                        }
                        
                    }} text="Remove" />
                    <DefaultButton onClick={closeDialog} text="Cancel" />
                </DialogFooter>
                
            </Dialog>
        )
    }
     
    private renderHostedSalamanderMessageBar(): JSX.Element | null
    {
        const hostedDataServiceType = MisDataServiceTypes.SalamanderHosted;

        if(this.state.originalDto?.mailroomSchoolConfig.misDataServiceType === hostedDataServiceType || this.state.editedDto?.mailroomSchoolConfig.misDataServiceType === hostedDataServiceType)
        {
            return (
                <MessageBar
                    className={"message-bar--inner-text-flex"}
                    messageBarType={MessageBarType.warning}
                    isMultiline={true}
                >
                    <div className={"flex-item--flex-1"}>
                    <span className={"text-tiny"}><b>MIS data may need to be re-fetched when using {hostedDataServiceType}.</b><br></br>
                        If you are turning on new options or using strategies that require additional data, <b>save the config</b> and<Link to={`/cdb-admin/mis-fetch/${this.props.tenantId}/${this.props.schoolCode}`} target="_blank"><b>fetch new MIS data</b></Link> before previewing changes.
                    </span>
                    </div>
            </MessageBar>);
        }
        return null;
    }

    public render() {

        const previewButton = (
            <CommandBarButton
                iconProps={{ iconName: "play" }}
                text="Preview processor without saving"
                disabled={this.state.editedDto === undefined || this.state.saveInProgress}
                onClick={(e) => this.previewStrategyAndOptions()} />
        );

        const saveButton = (
            <CommandBarButton
                iconProps={{ iconName: "save" }}
                text="Save All"
                disabled={!this.state.canSaveConfig || !this.unsavedChangesAreValidAndCanBeSaved()}
                onClick={(e) => this.save()} />
        );

        return (
            <Fragment>
                <div className={`card`}>
                    <div className="card__header">
                        <span className={"card__title"}>Config</span>
                        {previewButton}
                        {saveButton}
                    </div>
                    {!this.state.canSaveConfig ? this.renderSaveDisabledMessageBar() : null}
                    {this.renderHostedSalamanderMessageBar()}
                    <div className="card__content">
                        {this.renderMailroomConfig()}
                    </div>

                    {this.renderSubjectEditor()}
                </div>

                {this.renderDeleteConfigCard()}
                {this.renderDeleteConfigDialog()}

                <Prompt message={"Leave page? Changes that you made may not be saved."} when={this.areUnsavedChanges()}></Prompt>;

            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { schoolCode: string, tenantId: string }): MailroomConfigureProps => {
        return {
            history: state.history,
            usersCdbAccessLevel: state.auth.usersCdbAccessLevel,
            schoolCode: ownProps.schoolCode,
            tenantId: ownProps.tenantId,
            apiService: state.auth.apiService
        };
    }
)(MailroomConfigure);